import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { sentence } from "@/utils/coach";
import { Badge } from "@/atoms/badge";
import { Icon } from "@/atoms/icon";
import { PriceDisplay } from "@/components/price-display";
import { promotionalBadgePreset } from "@/data/promotional-badges";
import { WordMenu } from "@/components/word-menu";
import { cadenceLine, durationPhrase, everyPhrase } from "@/utils/interval";
import { fromCents, splitDisplayMoney } from "@/utils/money";
import type {
  PublicCheckoutProduct,
  PublicCheckoutProductPrice,
} from "@/data/checkout";
import type { TFunction } from "i18next";

/**
 * What is being bought (checkout 1.4 region `product`; specs price-display 1.3, plan-card).
 *
 * The product tile, the name as the page's h1, the amount as the largest number on the page,
 * what is included, and the cadence sentence. Everything else the old page carried is gone: no
 * cover image (a checkout page that sells is a checkout page that leaks), no emoji avatar drawn
 * as UI (gate 7.5, and the real payload sends `avatar.type === "emoji"` today), and no green
 * "you are saving" line, because the saving is already said by the struck price and the
 * discount row in the totals.
 *
 * The h1 takes focus on load, so a payer arriving by keyboard or with a screen reader lands on
 * what they are buying rather than on the language control (checkout 1.4 focusOnLoad).
 */

export interface ProductBlockProps {
  product: PublicCheckoutProduct;
  price: PublicCheckoutProductPrice;
  isSubscription: boolean;
  /** The total the page is showing, in cents, discount already applied. */
  totalCents: number;
  /** The total before the discount, in cents; only drawn when a code has been accepted. */
  previousTotalCents: number | null;
  currency: string;
  availableCurrencies: string[];
  onCurrencyChange: (currency: string) => void;
  /** Already resolved: the coach's own name, or the words that stand in for it. */
  coachName: string;
}

export function ProductBlock({
  product,
  price,
  isSubscription,
  totalCents,
  previousTotalCents,
  currency,
  availableCurrencies,
  onCurrencyChange,
  coachName,
}: ProductBlockProps) {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const { figure, unit } = splitDisplayMoney(fromCents(totalCents), currency);
  const previous =
    previousTotalCents !== null && previousTotalCents > totalCents
      ? splitDisplayMoney(fromCents(previousTotalCents), currency).figure
      : null;

  const recurring = price.recurring ?? null;
  /* The cadence is written to sit after an amount ("79 EUR a month"), and under the figure it
     is a line of its own. Capitalised here rather than with a CSS `::first-letter`: gate 6.3
     measures `text-transform: uppercase` on body-size text and fails it, and `sentence()` is the
     helper this app already uses for a string that becomes the head of a line. The inline uses,
     in the totals caption and on the portal rows, stay lowercase. */
  const cadence = sentence(
    isSubscription && recurring
      ? cadenceLine(t, recurring)
      : cadenceLine(t, null),
  );

  const commitment = price.commitment ?? product.commitment ?? null;
  const commitmentPhrase = commitment ? durationPhrase(t, commitment) : null;

  const badgePreset = promotionalBadgePreset(
    product.branding?.promotionalBadge,
  );
  const badge = badgePreset
    ? t(`gymkeePay.checkout.badge.${badgePreset.key}`)
    : product.branding?.promotionalBadge;
  const description = product.description?.trim();
  const included = product.highlights?.included ?? [];
  const notIncluded = product.highlights?.notIncluded ?? [];
  const billedPhrase =
    isSubscription && recurring ? everyPhrase(t, recurring) : null;

  const image =
    product.avatar?.type === "image" ? product.avatar.value : undefined;
  const deliveryLine = deliverySentence(t, product, coachName);

  return (
    <section className="gap-gk-4 flex flex-col">
      <div className="gap-gk-4 flex flex-col sm:flex-row sm:items-start">
        {/* The tile only when the coach gave the product a picture. A glyph in an empty square
            reads as a picture that failed to load, and a page that sells one thing does not need
            an icon to say which thing. */}
        {image && <ProductTile image={image} />}
        <div className="gap-gk-2 flex min-w-0 flex-1 flex-col">
          {badge && (
            <Badge
              tone="warning"
              className={badgePreset ? "gap-gk-1 self-start" : "self-start"}
            >
              {badgePreset && (
                <img
                  src={badgePreset.object}
                  alt=""
                  width={16}
                  height={16}
                  className="h-4 w-4 flex-none object-contain"
                />
              )}
              {badge}
            </Badge>
          )}
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="ds-pay-lede text-[24px] leading-[1.2] font-semibold text-[var(--gk-text-primary)]"
          >
            {product.name}
          </h1>
          {/* What the athlete gets and where, from the product's own delivery: the question a
              person asks between reading the name and reading the price. */}
          {deliveryLine && (
            <p className="text-[14px] leading-[1.5] text-[var(--gk-text-secondary)]">
              {deliveryLine}
            </p>
          )}
          {description && (
            <p className="text-[15px] leading-[1.55] text-[var(--gk-text-secondary)]">
              {description}
            </p>
          )}
        </div>
      </div>

      <div className="gap-gk-3 flex flex-wrap items-end justify-between">
        <PriceDisplay
          figure={figure}
          unit={unit}
          previousAmount={previous}
          cadence={cadence}
          note={
            commitmentPhrase
              ? t("gymkeePay.checkout.commitment", {
                  duration: commitmentPhrase,
                })
              : null
          }
        />
        {availableCurrencies.length > 1 && (
          <WordMenu
            name={t("gymkeePay.common.currencyMenu")}
            triggerLabel={t("gymkeePay.common.payInCurrency", {
              currency: currency.toUpperCase(),
            })}
            value={currency}
            options={availableCurrencies.map((code) => ({
              value: code,
              label: code.toUpperCase(),
            }))}
            onChange={onCurrencyChange}
          />
        )}
      </div>

      {(included.length > 0 || notIncluded.length > 0) && (
        <ul className="flex flex-col">
          {included.map((line) => (
            <li
              key={`in-${line.text}`}
              className="gap-gk-3 flex min-h-[40px] items-center text-[14px] text-[var(--gk-text-secondary)]"
            >
              {/* The crescent is the mark that says a line is part of what you get. Ink, never
                  orange: orange's one job on this page is the Pay button. */}
              <Icon
                name="set-done"
                size={20}
                style={{ color: "var(--gk-text-primary)", flexShrink: 0 }}
              />
              <span className="min-w-0">{line.text}</span>
            </li>
          ))}
          {notIncluded.map((line) => (
            <li
              key={`out-${line.text}`}
              className="gap-gk-3 flex min-h-[40px] items-center text-[14px] text-[var(--gk-text-tertiary)]"
            >
              <Icon name="close" size={20} style={{ flexShrink: 0 }} />
              <span className="min-w-0">{line.text}</span>
            </li>
          ))}
        </ul>
      )}

      {billedPhrase && (
        <p className="text-[13px] text-[var(--gk-text-tertiary)]">
          {t("gymkeePay.checkout.cancelAnyTime", { cadence: billedPhrase })}
        </p>
      )}
    </section>
  );
}

/**
 * The product's picture: the 48 square at radius control, and only when the coach gave one.
 *
 * `avatar.type === "emoji"` is deliberately not drawn. The real link this lane was built against
 * sends a trophy emoji, and an emoji rendered as the product's mark is an emoji used as UI,
 * which the system bans outright (gate 6.3 and 7.5). The coach's emoji is not lost: it is his
 * own content on the coach side, and here the h1 carries the product on its own.
 */
function ProductTile({ image }: { image: string }) {
  return (
    <span
      className="rounded-control bg-surface-paperMuted flex h-[48px] w-[48px] flex-none items-center justify-center overflow-hidden"
      aria-hidden="true"
    >
      <img
        src={image}
        alt=""
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    </span>
  );
}

/** What happens after the payment, from the product's own delivery method. */
function deliverySentence(
  t: TFunction,
  product: PublicCheckoutProduct,
  coachName: string,
): string | null {
  const delivery = (product.deliveryMethod ?? "gymkee").toLowerCase();
  if (delivery === "service" && product.serviceConfig) {
    return t("gymkeePay.checkout.deliveryService", {
      type: t(serviceTypeKey(product.serviceConfig.type)),
      duration: product.serviceConfig.duration,
    });
  }
  if (delivery === "external") {
    return sentence(
      t("gymkeePay.checkout.deliveryExternal", { coach: coachName }),
    );
  }
  if (
    delivery === "gymkee" ||
    delivery === "program" ||
    delivery === "programme"
  ) {
    return t("gymkeePay.checkout.deliveryGymkee");
  }
  // A delivery the app does not know says nothing rather than guessing where the thing lands.
  return null;
}

function serviceTypeKey(type: string): string {
  if (type === "visio") return "gymkeePay.receipt.serviceVisio";
  if (type === "mixed") return "gymkeePay.receipt.serviceMixed";
  return "gymkeePay.receipt.serviceInPerson";
}
